/**
 * Reconciliation New Service
 * API service for the new reconciliation system with hash-based transactions
 */

import request from './request';
import useSWR from 'swr';

const BASE_URL = '/reconciliation-new';

// Raw API functions
export const uploadStatement = async (formData) => {
  return request({
    url: `${BASE_URL}/upload-statement`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getStatements = async (companyId, params = {}) => {
  const queryParams = new URLSearchParams({
    ...params,
  }).toString();

  return request({
    url: `${BASE_URL}/statements/${companyId}?${queryParams}`,
    method: 'GET',
  });
};

export const getStatementTransactions = async (statementId, params = {}) => {
  const queryParams = new URLSearchParams({
    page: params.page || 1,
    limit: params.limit || 50,
    ...params,
  }).toString();

  return request({
    url: `${BASE_URL}/transactions/${statementId}?${queryParams}`,
    method: 'GET',
  });
};

export const findMatchingTransactions = async (data) => {
  return request({
    url: `${BASE_URL}/match-transactions`,
    method: 'POST',
    data,
  });
};

export const performReconciliation = async (data) => {
  return request({
    url: `${BASE_URL}/reconcile`,
    method: 'PUT',
    data,
  });
};

export const getReconciliationStats = async (companyId, bankAccountId = null) => {
  const params = bankAccountId ? `?bankAccountId=${bankAccountId}` : '';
  return request({
    url: `${BASE_URL}/stats/${companyId}${params}`,
    method: 'GET',
  });
};

export const deleteStatement = async (statementId, companyId) => {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  return request({
    url: `${BASE_URL}/statement/${statementId}${query}`,
    method: 'DELETE',
  });
};

export const removeReconciliation = async (reconciliationId, companyId, userId) => {
  return request({
    url: `${BASE_URL}/unreconcile`,
    method: 'POST',
    data: {
      reconciliationId,
      companyId,
      userId,
    },
  });
};

export const unreconcileAllByStatement = async (statementId, companyId, userId) => {
  return request({
    url: `${BASE_URL}/unreconcile-by-statement`,
    method: 'POST',
    data: {
      statementId,
      companyId,
      userId,
    },
  });
};

// SWR Hooks for optimized data fetching
export const useQueryStatements = (companyId, options = {}) => {
  const { enabled = true, refreshInterval = null, ...swrOptions } = options;

  const cacheKey =
    enabled && companyId ? [`reconciliation-new-statements`, companyId, options] : null;

  const { data, error, mutate, isLoading, isValidating } = useSWR(
    cacheKey,
    () => getStatements(companyId, options),
    {
      refreshInterval,
      revalidateOnFocus: true, // Enable revalidate on focus to ensure fresh data
      dedupingInterval: 5000, // Reduce deduping interval for more frequent updates
      ...swrOptions,
    }
  );

  // Simple validation and error handling
  if (data?.data && !Array.isArray(data.data)) {
    console.warn('[Statements API] Invalid data structure - expected array');
  }

  return {
    statements: data?.data || [], // Handle new API format: { success, data, total }
    total: data?.total || 0, // Extract total from root level
    isLoading,
    isValidating,
    error,
    mutate,
  };
};

export const useQueryStatementTransactions = (statementId, options = {}) => {
  const { enabled = true, refreshInterval = null, ...swrOptions } = options;

  const cacheKey =
    enabled && statementId ? [`reconciliation-new-transactions`, statementId, options] : null;

  const { data, error, mutate, isLoading, isValidating } = useSWR(
    cacheKey,
    () => getStatementTransactions(statementId, options),
    {
      refreshInterval,
      revalidateOnFocus: false,
      dedupingInterval: 15000, // 15 seconds
      ...swrOptions,
    }
  );

  return {
    transactions: data?.data?.data || [],
    pagination: data?.data?.pagination || {},
    isLoading,
    isValidating,
    error,
    mutate,
  };
};

export const useQueryReconciliationStats = (companyId, bankAccountId = null, options = {}) => {
  const { enabled = true, refreshInterval = 30000, ...swrOptions } = options;

  const cacheKey =
    enabled && companyId ? [`reconciliation-new-stats`, companyId, bankAccountId] : null;

  const { data, error, mutate, isLoading, isValidating } = useSWR(
    cacheKey,
    () => getReconciliationStats(companyId, bankAccountId),
    {
      refreshInterval,
      revalidateOnFocus: true,
      dedupingInterval: 10000, // 10 seconds
      ...swrOptions,
    }
  );

  return {
    stats: data?.data?.stats || {},
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
